import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { llmKeys } from '~/lib/query-keys';
import { useVoiceModels } from './use-voice-models';
import type { SettingsResponse } from '~/features/settings/models-section';

/**
 * FRONT-1034: resolves the effective STT model for mic dictation, shared by
 * every dictation surface (chat composer, AiTextarea) so they can't
 * disagree on which model is "current" for a user.
 *
 * Resolution order: the user's persisted pick (Settings → Models) if it's
 * still in the live catalogue, else the server default if THAT is still
 * valid, else the first available model. The server default is
 * re-validated (not just trusted) because it can drift from the live
 * catalogue independently (env var vs. upstream model list).
 */
export function useSttModel(): string {
  const { data: voiceModels } = useVoiceModels();
  const { data: llmSettings } = useQuery({
    queryKey: llmKeys.settings(),
    queryFn: () => api.get<SettingsResponse>('/api/llm/settings'),
    staleTime: 60_000,
  });

  const saved = llmSettings?.overrides.sttModel;
  const ids = new Set(voiceModels?.models.map((m) => m.id) ?? []);
  if (saved && ids.has(saved)) return saved;
  if (voiceModels?.default && ids.has(voiceModels.default)) return voiceModels.default;
  return voiceModels?.models[0]?.id ?? '';
}
